/** Checking compiled wiki pages against a code repository with the user's own agent (no server LLM) */

import { z } from 'zod'
import { MAX_WIKI_PAGE_CHARS, SOURCE_ORIGIN_PATTERN } from './project'
import type { WikiWriteResult } from '../types/wiki-review'

export const WIKI_CHECK_STATUSES = ['confirmed', 'outdated', 'unknown'] as const
export type WikiCheckStatus = (typeof WIKI_CHECK_STATUSES)[number]

/** Origin of Sources pushed from a repository as evidence for a check */
export const CODE_SOURCE_ORIGIN = 'code'

/** Marker an agent writes in a corrected page to cite a repository file; replaced by [src:<id>] on propose */
export const CODE_CITATION_MARKER = /\[code:([^\]\s]+)\]/g

/** Repository path of a [code:...] reference: drops `./`, a `:line` / `#Lx-Ly` anchor, and backslashes */
export function normalizeCodeRef(ref: string): string {
  return ref.replace(/#L\d+(?:-L?\d+)?$/i, '').replace(/:\d+(?:-\d+)?$/, '').replace(/^\.\//, '').replaceAll('\\', '/')
}

/** Repository paths cited as [code:<path>], in first-seen order */
export function findCodeCitations(markdown: string): string[] {
  return [...new Set([...markdown.matchAll(CODE_CITATION_MARKER)].map((m) => normalizeCodeRef(m[1])))]
}

/** Swap every [code:<path>] marker for [src:<id>] of the Source pushed from that path; throws for an unknown path */
export function replaceCodeCitations(markdown: string, sourceIdByPath: Map<string, string>): string {
  return markdown.replace(CODE_CITATION_MARKER, (marker, ref: string) => {
    const id = sourceIdByPath.get(normalizeCodeRef(ref))
    if (!id) throw new Error(`No Source was pushed for ${marker}`)
    return `[src:${id}]`
  })
}

/** File names that hold credentials, matched against the whole (lower-cased) name */
const SECRET_FILENAMES = new Set([
  '.dev.vars', '.npmrc', '.pypirc', '.netrc', '.pgpass', '.git-credentials', '.htpasswd',
  'kubeconfig', 'id_rsa', 'id_dsa', 'id_ecdsa', 'id_ed25519',
])
const SECRET_EXTENSIONS = ['.pem', '.key', '.p12', '.pfx', '.jks', '.keystore']
/** Directories whose contents are credentials */
const SECRET_DIRECTORIES = new Set(['.aws', '.ssh', '.gnupg', '.kube', 'secrets', '.git'])

/**
 * Why a repository path must not be uploaded as evidence, or null. Matches whole file names, extensions, and
 * directory segments, never substrings: `token-service.ts`, `secrets.ts`, and `password-reset.ts` are code.
 */
export function sensitivePathReason(relPath: string): string | null {
  const segments = relPath.split(/[\\/]+/).filter(Boolean).map((s) => s.toLowerCase())
  const name = segments[segments.length - 1] ?? ''
  const dir = segments.slice(0, -1).find((s) => SECRET_DIRECTORIES.has(s))
  if (dir) return `it is inside a ${dir}/ directory`
  if (name.startsWith('.env')) return 'it is an environment file'
  if (SECRET_FILENAMES.has(name)) return `${name} holds credentials`
  if (/^credentials(?:$|[._-])/.test(name)) return 'it is a credentials file'
  if (/\.tfstate(?:\.|$)/.test(name)) return 'Terraform state holds secrets'
  const ext = SECRET_EXTENSIONS.find((e) => name.endsWith(e))
  if (ext) return `${ext} files hold keys`
  return null
}

const SECRET_PATTERNS: Array<{ label: string; pattern: RegExp }> = [
  { label: 'a private key', pattern: /-----BEGIN [A-Z0-9 ]*PRIVATE KEY( BLOCK)?-----/ },
  { label: 'an AWS access key', pattern: /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/ },
  { label: 'a GitHub token', pattern: /\b(?:gh[pousr]_[A-Za-z0-9]{36,}|github_pat_[A-Za-z0-9_]{22,})/ },
  { label: 'a Slack token', pattern: /\bxox[abprse]-[A-Za-z0-9-]{10,}/ },
  { label: 'a Stripe key', pattern: /\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{16,}/ },
  { label: 'an AgentWiki API key', pattern: /\baw_[A-Za-z0-9_-]{32,}/ },
]

/** Label of the first recognizable secret in the text, or null */
export function findSecret(text: string): string | null {
  return SECRET_PATTERNS.find((p) => p.pattern.test(text))?.label ?? null
}

/** First line (1-based) holding a recognizable secret, with what it looks like; never returns the secret itself */
export function findSecretLine(text: string): { line: number; label: string } | null {
  const lines = text.split(/\r?\n/)
  for (let i = 0; i < lines.length; i++) {
    const label = findSecret(lines[i])
    if (label) return { line: i + 1, label }
  }
  // Patterns that span lines (none today) still count
  const label = findSecret(text)
  return label ? { line: 1, label } : null
}

/**
 * POST /api/projects/:project/wiki/:slug/check-proposal: a correction found by checking a page against code.
 * The server validates everything, pushes the evidence as Sources, and proposes the page; the proposal always
 * waits for a person, and a failure undoes the pushes.
 */
export const wikiCheckProposalSchema = z.object({
  /** default: the page's current title */
  title: z.string().trim().min(1).max(200).optional(),
  /** corrected page; changed sentences cite repository files as [code:<path>] */
  content: z.string().min(1).max(MAX_WIKI_PAGE_CHARS),
  /** the page version the check read (from the brief) */
  expectedVersion: z.number().int().min(1),
  change: z.string().trim().max(400).optional(),
  repo: z.object({
    /** display name, e.g. agentwiki */
    name: z.string().trim().min(1).max(200),
    /** stable identity, e.g. github.com/org/agentwiki; evidence Sources use originRef `repo:<ref>` */
    ref: z.string().trim().min(1).max(400),
    commit: z.string().trim().regex(/^[0-9a-f]{7,64}$/i, 'commit must be a git SHA'),
    dirty: z.boolean().default(false),
  }),
  evidence: z.array(z.object({
    path: z.string().trim().min(1).max(255),
    content: z.string().min(1),
  })).max(50).default([]),
  origin: z.string().trim().toLowerCase().regex(SOURCE_ORIGIN_PATTERN).default(CODE_SOURCE_ORIGIN),
})

export type WikiCheckProposalInput = z.input<typeof wikiCheckProposalSchema>

/** Result of a check proposal: the review-queue write plus the evidence Sources it cites */
export type WikiCheckProposalResult = WikiWriteResult & {
  sources: Array<{ path: string; id: string; deduplicated: boolean; supersededId: string | null }>
}

export const wikiCheckBriefQuerySchema = z.object({
  /** comma-separated page slugs; all pages when absent */
  pages: z.string().trim().max(2000).optional(),
})

/** Findings the agent writes after a check; `agentwiki check --report` renders them */
export const wikiCheckFindingsSchema = z.object({
  project: z.string().trim().max(100).optional(),
  repo: z.object({
    name: z.string().trim().min(1).max(200),
    commit: z.string().trim().min(4).max(64),
  }).optional(),
  pages: z.array(z.object({
    slug: z.string().trim().min(1).max(120),
    version: z.number().int().min(1).optional(),
    proposalId: z.string().trim().max(100).optional(),
    claims: z.array(z.object({
      claim: z.string().trim().min(1).max(2000),
      status: z.enum(WIKI_CHECK_STATUSES),
      /** `path` or `path:line` in the repository */
      evidence: z.array(z.string().trim().min(1).max(500)).default([]),
      note: z.string().trim().max(2000).optional(),
    })),
  })),
})

export type WikiCheckFindings = z.infer<typeof wikiCheckFindingsSchema>

/** GET /api/projects/:project/check-brief */
export interface WikiCheckBrief {
  project: { id: string; slug: string; name: string; repoUrl: string | null }
  instructions: string
  pages: Array<{
    slug: string
    title: string
    topic: string | null
    version: number
    updatedAt: number
    pinnedAnchors: string[]
    content: string
    /** Sources the page cites, with where they came from */
    sources: Array<{ id: string; title: string; filename: string; origin: string; originRef: string | null; status: string }>
  }>
  /** some pages were left out because the brief hit its size cap; check them by slug */
  truncated: boolean
}

export const WIKI_CHECK_INSTRUCTIONS = `Check every page below against the code in the repository. You do the reading and judging; AgentWiki runs no model for this.

For each page:
1. Read the page and split it into factual claims (one sentence or bullet each; skip headings and opinions).
2. Find the code for each claim: grep the repository for the names, paths, commands, config keys, and numbers it mentions, then open the files. The page's cited Sources show where a claim came from.
3. Mark each claim:
   - confirmed: the code at this commit does what the claim says (evidence: path:line)
   - outdated: the code contradicts it (evidence, and what is true now)
   - unknown: no code settles it (say where you looked)
4. Only for a page with outdated claims, write the corrected page in full. Keep pinned sections and every unchanged sentence with its [src:<id>] citation. End each sentence you change or add with [code:<repo-relative path>] for the git-tracked file it relies on (never a credentials or .env file). Then propose it with the page version from this brief:
   - CLI: agentwiki check <project> --propose <slug> --file <page.md> --expected-version <version> --path <repo>
   - MCP: wiki_check_propose with the page, expectedVersion, the repository (name, host/org/repo ref, commit, dirty), and the content of each cited file.
   AgentWiki pushes each cited file as a Source titled "<path> @ <short sha>", replaces [code:...] with [src:<id>], and puts the edit in the review queue. Check corrections always wait for a person.
5. Write the findings as JSON: { "repo": { "name", "commit" }, "pages": [{ "slug", "version", "proposalId"?, "claims": [{ "claim", "status", "evidence": ["path:line"], "note"? }] }] }
   and print the report with: agentwiki check <project> --report findings.json

Rules: propose only to fix outdated claims, never to rephrase. Never copy secrets, tokens, or credentials from the repository into a page or a Source.`
